import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { ArrowLeft, Check, Users, Building2, Shield, Zap, Headphones, CreditCard, Plus, Minus } from "lucide-react";
import billingService from "../../services/billingService";
import { showApiMessage } from "../../utils/messageHandler";
import * as localService from "../../services/localService";
import { Container } from "../../components/ui/container";
import { useCurrencies } from "../../hooks/useCurrencies";
import {
  checkActiveSubscription,
  getSubscriptionQuantity,
  isQuantityEditable,
  calculateSubscriptionCost,
} from "../../utils/subscriptionUtils";
import { isOrgAdmin } from "../../utils/orgAdminAccess";
import ContactAdminBanner from "../../components/ContactAdminBanner";
import ContactAdminModal from "../../components/ContactAdminModal";
import logger from "../../utils/logger";

const SubscriberSubscriptionUpgrade = () => {
  const history = useHistory();
  const location = useLocation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const packageId = new URLSearchParams(location.search).get("packageId");
  const { currencies } = useCurrencies();

  useEffect(() => {
    const user = localService.getUser();
    setCurrentUser(user);
  }, []);

  // Log currencies when they change
  useEffect(() => {
    logger.log("SUBSCRIBER SUBSCRIPTION UPGRADE: Currencies updated, count:", currencies.length);
    if (currencies.length > 0) {
      logger.log("Sample currencies:", currencies.slice(0, 3));
    }
  }, [currencies]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!packageId) throw new Error("packageId required");
        const resp = await billingService.getApplicablePackageById(packageId);
        if (resp.data?.data) {
          const pkg = resp.data.data;
          setSelectedPackage(pkg);
          // Initialize quantity based on package
          const user = localService.getUser();
          if (user) {
            // For add-ons (recurring = false), always start with quantity 1
            // For recurring packages, use the calculated quantity
            const isAddOn = pkg.recurring === false;
            const initialQuantity = isAddOn ? 1 : getSubscriptionQuantity(user, pkg);
            setQuantity(initialQuantity);
          }
        }
      } catch (e) {
        showApiMessage(e, "load package");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [packageId]);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const userIsOrgAdmin = isOrgAdmin(currentUser);

  const handleSubscribe = async () => {
    if (!userIsOrgAdmin) {
      setContactModalOpen(true);
      return;
    }

    try {
      setSubmitting(true);

      const user = localService.getUser();
      if (!user?.organizationId) {
        throw new Error("Organization ID not found. Please ensure you're logged in.");
      }

      // For add-ons (recurring = false), always use the quantity state
      // For recurring packages, use quantity state if editable, otherwise use calculated quantity
      const isAddOn = selectedPackage?.recurring === false;
      const subscriptionQuantity = getSubscriptionQuantity(user, selectedPackage);
      const finalQuantity = isAddOn
        ? quantity
        : isQuantityEditable(user, selectedPackage)
          ? quantity
          : subscriptionQuantity;

      const userRole = user?.organizationRole?.code || user?.role || "";

      // Check if user has an active subscription
      const activeSubscriptionCheck = await checkActiveSubscription(user);

      let response;
      const isRecurringPackage = selectedPackage?.recurring !== false;

      if (
        isRecurringPackage &&
        activeSubscriptionCheck.hasActiveSubscription &&
        activeSubscriptionCheck.activeSubscription?.id
      ) {
        // User has active subscription - use CHANGE API
        logger.log("Changing existing subscription:", {
          organizationId: user.organizationId,
          subscriptionId: activeSubscriptionCheck.activeSubscription.id,
          packageId,
          quantity: finalQuantity,
          userRole: userRole,
        });

        response = await billingService.changeOrganizationSubscription({
          organizationId: user.organizationId,
          subscriptionId: activeSubscriptionCheck.activeSubscription.id,
          packageId,
          quantity: finalQuantity,
        });
      } else {
        // User has no active subscription - use CREATE API
        logger.log("Creating new subscription:", {
          organizationId: user.organizationId,
          packageId,
          quantity: finalQuantity,
          userRole: userRole,
        });

        response = await billingService.createOrganizationSubscription({
          organizationId: user.organizationId,
          packageId,
          quantity: finalQuantity,
        });
      }

      logger.log("Subscription response:", response);

      // Check for success message first
      const successOperation =
        isRecurringPackage &&
        activeSubscriptionCheck.hasActiveSubscription &&
        activeSubscriptionCheck.activeSubscription?.id
          ? "change subscription"
          : "create subscription";

      if (response?.data?.message) {
        showApiMessage(response, successOperation);
      }

      // Try multiple possible paths for checkout URL
      const checkoutUrl =
        response?.data?.data?.checkoutUrl ||
        response?.data?.checkoutUrl ||
        response?.data?.data?.paymentUrl ||
        response?.data?.paymentUrl ||
        response?.data?.data?.url ||
        response?.data?.url;

      logger.log("Checkout URL found:", checkoutUrl);

      if (checkoutUrl && typeof checkoutUrl === "string") {
        // Direct navigation to checkout URL
        window.location.href = checkoutUrl;
        return;
      }

      // If no checkout URL, show success and redirect to dashboard
      showApiMessage(
        {
          data: {
            message:
              successOperation === "change subscription"
                ? "Subscription updated successfully! Redirecting to dashboard..."
                : "Subscription created successfully! Redirecting to dashboard...",
          },
        },
        successOperation
      );

      setTimeout(() => {
        history.push("/dashboard");
      }, 2000);
    } catch (e) {
      logger.error("Subscription error:", e);
      showApiMessage(e, "create subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount, currencyIdOrCode = "KES") => {
    logger.log(
      "SUBSCRIBER formatCurrency called with amount:",
      amount,
      "currencyIdOrCode:",
      currencyIdOrCode,
      "currencies available:",
      currencies.length
    );
    if (!amount) return "KES 0";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    // Determine currency code from ID or use the provided code
    let currencyCode = "KES";
    if (typeof currencyIdOrCode === "number") {
      // It's a currency ID, look it up from currencies
      logger.log("Looking up currency by ID:", currencyIdOrCode);
      const currency = currencies.find(c => c.id === currencyIdOrCode);
      logger.log("Found currency:", currency);
      if (currency) {
        currencyCode = currency.code;
        logger.log("Using currency code:", currencyCode);
      } else {
        logger.warn("Currency not found for ID:", currencyIdOrCode, "using KES");
      }
    } else if (typeof currencyIdOrCode === "string") {
      // It's already a currency code
      logger.log("Using provided currency code:", currencyIdOrCode);
      currencyCode = currencyIdOrCode;
    } else if (currencyIdOrCode && typeof currencyIdOrCode === "object" && currencyIdOrCode.code) {
      // It's a currency object with a code property
      logger.log("Using currency code from object:", currencyIdOrCode.code);
      currencyCode = currencyIdOrCode.code;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const getPackageFeatures = pkg => {
    const features = [];

    if (pkg.maximumTeamSize) {
      features.push({
        text: `${pkg.maximumTeamSize} team members`,
        icon: Users,
        description: "Maximum number of team members allowed",
      });
    }

    if (pkg.maximumServiceProvidersAllowed) {
      features.push({
        text: `${pkg.maximumServiceProvidersAllowed} service providers`,
        icon: Building2,
        description: "Maximum number of service providers",
      });
    }

    if (pkg.offers && pkg.offers.length > 0) {
      pkg.offers.forEach(offer => {
        if (offer.name) {
          features.push({
            text: offer.name,
            icon: Zap,
            description: offer.description || "Included service offer",
          });
        }
      });
    }

    // Add default features if none exist
    if (features.length === 0) {
      features.push(
        {
          text: "Core features included",
          icon: Check,
          description: "All essential platform features",
        },
        {
          text: "Basic support",
          icon: Headphones,
          description: "Email and chat support",
        },
        {
          text: "Standard templates",
          icon: Building2,
          description: "Access to standard templates",
        },
        {
          text: "Email support",
          icon: Check,
          description: "24/7 email support",
        },
        {
          text: "Cloud storage",
          icon: Shield,
          description: "Secure cloud storage included",
        }
      );
    }

    return features;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="px-4 py-16 mx-auto max-w-4xl">
          <div className="animate-pulse">
            <div className="mb-4 w-1/3 h-8 bg-gray-200 rounded"></div>
            <div className="mb-16 w-1/2 h-6 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="p-8 rounded-lg border border-gray-200">
                  <div className="mb-4 h-6 bg-gray-200 rounded"></div>
                  <div className="mb-6 h-12 bg-gray-200 rounded"></div>
                  <div className="mb-8 h-4 bg-gray-200 rounded"></div>
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map(j => (
                      <div key={j} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="p-8 rounded-lg border border-gray-200">
                  <div className="mb-4 h-6 bg-gray-200 rounded"></div>
                  <div className="mb-6 h-12 bg-gray-200 rounded"></div>
                  <div className="mb-8 h-12 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedPackage) {
    return (
      <div className="min-h-screen">
        <div className="px-4 py-16 mx-auto max-w-4xl">
          <div className="text-center">
            <h3 className="mb-2 text-xl font-semibold text-gray-900">Package not found</h3>
            <p className="mb-6 text-gray-600">The selected package could not be found.</p>
            <Button onClick={() => history.push("/subscriber/pricing")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Pricing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const features = getPackageFeatures(selectedPackage);

  // Calculate total cost based on quantity
  // For add-ons, always use quantity state. For recurring packages, use quantity state if editable, otherwise use calculated quantity
  const isAddOn = selectedPackage?.recurring === false;
  const calculatedQuantity = getSubscriptionQuantity(currentUser, selectedPackage);
  const finalQuantityForPricing = isAddOn
    ? quantity
    : isQuantityEditable(currentUser, selectedPackage)
      ? quantity
      : calculatedQuantity;
  const { perUserCost, totalCost } = calculateSubscriptionCost(selectedPackage, finalQuantityForPricing);

  return (
    <div className="min-h-screen">
      <Container className="px-4 py-16 mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-12">
          <Button
            onClick={() => history.push("/pricing")}
            variant="ghost"
            className="flex gap-2 items-center mb-6 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to pricing
          </Button>
          <h1 className="mb-4 text-3xl font-semibold text-gray-900">{selectedPackage.name}</h1>
          <p className="max-w-2xl text-lg text-gray-600">
            {selectedPackage.description || "Comprehensive package with all the features you need to get started"}
          </p>

          {!userIsOrgAdmin && (
            <div className="mt-6">
              <ContactAdminBanner user={currentUser} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          {/* Package Details */}
          <div className="lg:col-span-2">
            {/* Pricing Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-gray-900">Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Per User Cost */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">
                      {selectedPackage.recurring === false ? "Per item cost:" : "Per user cost:"}
                    </span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(perUserCost, selectedPackage.currencyId)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {selectedPackage.recurring === false ? "Quantity:" : "Number of users:"}
                    </span>
                    <span className="text-lg font-semibold text-gray-900">{finalQuantityForPricing}</span>
                  </div>
                </div>

                {/* Total Cost */}
                <div className="flex gap-3 items-baseline mb-4">
                  <span className="text-5xl font-bold text-gray-900">
                    {formatCurrency(totalCost, selectedPackage.currencyId)}
                  </span>
                  <span className="text-xl text-gray-600">{selectedPackage.recurring ? "/month" : "one-time"}</span>
                </div>
                {selectedPackage.recurring && <p className="text-gray-600">Billed monthly • Cancel anytime</p>}
                <p className="text-sm text-gray-500 mt-2">
                  {selectedPackage.recurring === false
                    ? `Total for ${finalQuantityForPricing} item${finalQuantityForPricing > 1 ? "s" : ""}`
                    : `Total for ${finalQuantityForPricing} user${finalQuantityForPricing > 1 ? "s" : ""}`}
                </p>
              </CardContent>
            </Card>

            {/* Features Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-gray-900">What's included</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {features.map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                      <div key={index} className="flex gap-4 items-start p-4 rounded-lg border border-gray-200">
                        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                          <IconComponent className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="mb-1 font-medium text-gray-900">{feature.text}</h3>
                          <p className="text-sm text-gray-600">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Package Details */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-gray-900">Package details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <h3 className="mb-2 font-medium text-gray-900">Package Information</h3>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Package ID:</span>
                          <span className="font-mono">{selectedPackage.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Code:</span>
                          <span>{selectedPackage.code || "N/A"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Type:</span>
                          <span className="capitalize">{selectedPackage.type?.toLowerCase() || "Standard"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Version:</span>
                          <span>{selectedPackage.version || "1.0"}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="mb-2 font-medium text-gray-900">Limits & Restrictions</h3>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Max Team Size:</span>
                          <span>{selectedPackage.maximumTeamSize || "Unlimited"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Max Service Providers:</span>
                          <span>{selectedPackage.maximumServiceProvidersAllowed || "Unlimited"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Recurring:</span>
                          <span>{selectedPackage.recurring ? "Yes" : "No"}</span>
                        </div>
                        {selectedPackage.validity && (
                          <div className="flex justify-between">
                            <span>Validity:</span>
                            <span>
                              {selectedPackage.validity} {selectedPackage.validityTimeUnit?.toLowerCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscribe Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-gray-900">Ready to subscribe?</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    {/* Quantity Input - Always show for add-ons, conditional for recurring packages */}
                    {(selectedPackage.recurring === false || isQuantityEditable(currentUser, selectedPackage)) && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <Label htmlFor="quantity" className="text-sm font-medium text-gray-700 mb-2 block">
                          {selectedPackage.recurring === false ? "Quantity" : "Number of Users"}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 flex-shrink-0"
                            onClick={() => {
                              const newQuantity = Math.max(1, quantity - 1);
                              setQuantity(newQuantity);
                            }}
                            disabled={quantity <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            id="quantity"
                            type="number"
                            min="1"
                            max={
                              selectedPackage.maximumTeamSize || (selectedPackage.recurring === false ? undefined : 1)
                            }
                            value={quantity}
                            onChange={e => {
                              const newQuantity = parseInt(e.target.value) || 1;
                              const maxAllowed =
                                selectedPackage.maximumTeamSize ||
                                (selectedPackage.recurring === false ? undefined : 1);
                              if (maxAllowed) {
                                setQuantity(Math.min(Math.max(1, newQuantity), maxAllowed));
                              } else {
                                setQuantity(Math.max(1, newQuantity));
                              }
                            }}
                            className="w-full text-center"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 flex-shrink-0"
                            onClick={() => {
                              const maxAllowed =
                                selectedPackage.maximumTeamSize ||
                                (selectedPackage.recurring === false ? undefined : 1);
                              if (maxAllowed) {
                                const newQuantity = Math.min(maxAllowed, quantity + 1);
                                setQuantity(newQuantity);
                              } else {
                                setQuantity(quantity + 1);
                              }
                            }}
                            disabled={
                              selectedPackage.maximumTeamSize ? quantity >= selectedPackage.maximumTeamSize : false
                            }
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {selectedPackage.maximumTeamSize && (
                          <p className="text-xs text-gray-500 mt-1">
                            Maximum: {selectedPackage.maximumTeamSize}{" "}
                            {selectedPackage.recurring === false ? "items" : "users"}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Breakdown */}
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {selectedPackage.recurring === false ? "Per item:" : "Per user:"}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(perUserCost, selectedPackage.currencyId)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {selectedPackage.recurring === false ? "Quantity:" : "Users:"}
                        </span>
                        <span className="font-medium text-gray-900">× {finalQuantityForPricing}</span>
                      </div>
                      <div className="h-px bg-gray-200"></div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700 font-medium">Total:</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(totalCost, selectedPackage.currencyId)}
                        </span>
                      </div>
                    </div>

                    {/* Large Total Display */}
                    <div className="flex gap-2 items-baseline mb-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatCurrency(totalCost, selectedPackage.currencyId)}
                      </span>
                      <span className="text-gray-600">{selectedPackage.recurring ? "/month" : "one-time"}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {selectedPackage.recurring
                        ? `Billed monthly for ${finalQuantityForPricing} user${finalQuantityForPricing > 1 ? "s" : ""} • Cancel anytime`
                        : `One-time payment for ${finalQuantityForPricing} item${finalQuantityForPricing > 1 ? "s" : ""}`}
                    </p>
                  </div>

                  <Button onClick={handleSubscribe} disabled={submitting} className="w-full mb-6" size="lg">
                    {submitting ? (
                      <div className="flex gap-2 justify-center items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                        Processing...
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-center items-center">
                        <CreditCard className="w-5 h-5" />
                        {userIsOrgAdmin ? "Subscribe Now" : "Contact Admin to Subscribe"}
                      </div>
                    )}
                  </Button>

                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex gap-2 items-center">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>30-day money-back guarantee</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Cancel anytime</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>24/7 support</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <ContactAdminModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          user={currentUser}
          packageName={selectedPackage?.name}
        />
      </Container>
    </div>
  );
};

export default SubscriberSubscriptionUpgrade;
